import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, AlertCircle, Trash2, Filter } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { FormikProps } from "formik";

import { ConditionItem } from "./ConditionItem";
import type { SilaeFormData, SilaeCondition } from "@/types/silae";
import {
  Card,
  CardTitle,
  CardContent,
  CardDescription,
  CardHeader,
} from "../ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Info } from "lucide-react";

interface AdvancedConditionsProps {
  formik: FormikProps<SilaeFormData>;
}

/**
 * Composant pour la configuration des conditions avancées SILAE
 */
export const AdvancedConditions: React.FC<AdvancedConditionsProps> = ({
  formik,
}) => {
  const { t } = useTranslation();
  const { values, errors, touched } = formik;
  const [activeTab, setActiveTab] = useState("condition-0");

  // Fonction pour générer un nouvel ID unique
  const generateId = () => Math.random().toString(36).substr(2, 9);

  const conditions = useMemo(
    () => values.conditions || [],
    [values.conditions],
  );

  // Fonction pour ajouter une nouvelle condition
  const addCondition = useCallback(() => {
    const newCondition: SilaeCondition = {
      id: generateId(),
      type: "silae_variable",
      silaeVariable: "",
      conditionOperator: "=",
      conditionValue: "",
      conditionPercentage: "100",
    };

    const updatedConditions = [...conditions, newCondition];
    formik.setFieldValue("conditions", updatedConditions);
    setActiveTab(`condition-${updatedConditions.length - 1}`);
  }, [conditions, formik]);

  // Fonction pour supprimer une condition
  const deleteCondition = useCallback(
    (index: number) => {
      if (conditions.length <= 1) return;
      const updatedConditions = conditions.filter((_, i) => i !== index);
      formik.setFieldValue("conditions", updatedConditions);

      // Ajuster les opérateurs si nécessaire
      // Si on a n conditions, on a n-1 opérateurs entre elles
      // Les opérateurs sont indexés entre les conditions : op[0] est entre cond[0] et cond[1]
      const ops = (values.conditionsOperators || []) as string[];
      if (ops.length > 0 && updatedConditions.length > 1) {
        const updatedOps: string[] = [];
        // On garde tous les opérateurs avant la condition supprimée
        for (let i = 0; i < index && i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        // On saute l'opérateur qui était après la condition supprimée (index)
        // et on garde tous les opérateurs suivants
        for (let i = index + 1; i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        // Si on n'a plus assez d'opérateurs, on ajoute des AND par défaut
        while (updatedOps.length < updatedConditions.length - 1) {
          updatedOps.push("AND");
        }
        formik.setFieldValue("conditionsOperators", updatedOps);
      } else if (updatedConditions.length === 1) {
        // Si on n'a plus qu'une condition, on supprime tous les opérateurs
        formik.setFieldValue("conditionsOperators", []);
      }

      // Ajuster l'onglet actif
      if (activeTab === `condition-${index}`) {
        const newIndex = Math.max(0, index - 1);
        setActiveTab(`condition-${newIndex}`);
      } else {
        // Mettre à jour l'index de l'onglet actif si nécessaire
        const currentTabIndex = parseInt(activeTab.split("-")[1] || "0");
        if (currentTabIndex > index) {
          setActiveTab(`condition-${currentTabIndex - 1}`);
        }
      }
    },
    [conditions, formik, values.conditionsOperators, activeTab],
  );

  // Déterminer si on affiche les conditions (pour with_conditions et if_else)
  const showConditions = values.structureType !== "simple";

  // Initialiser automatiquement une condition quand les conditions sont activées
  useEffect(() => {
    if (showConditions && conditions.length === 0) {
      addCondition();
    }
  }, [showConditions, conditions.length, addCondition]);

  // Si on est en mode "simple", ne rien afficher
  if (!showConditions) {
    return null;
  }

  return (
    <Card className="bg-background dark:bg-background mb-0">
      <div className="flex w-full items-center justify-between px-6">
        <div className="">
          <CardTitle className="text-lg">
            {t("advancedConditions.title")}
            {/* Exemples de conditions */}
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0 rounded-full ml-2"
                >
                  <Info className="h-4 w-4" />
                </Button>
              </DialogTrigger>

              <DialogContent className="p-0 border-0">
                <Card className="bg-blue-50 dark:bg-blue-950/20">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      {t("advancedConditions.examplesTitle")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p className="font-medium mb-3">
                        {t("advancedConditions.examplesSubtitle")}
                      </p>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• {t("advancedConditions.examples.seasonal")}</li>
                        <li>• {t("advancedConditions.examples.seniority")}</li>
                        <li>
                          • {t("advancedConditions.examples.performance")}
                        </li>
                        <li>• {t("advancedConditions.examples.service")}</li>
                        <li>• {t("advancedConditions.examples.multiple")}</li>
                        <li>• {t("advancedConditions.examples.cumul")}</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </DialogContent>
            </Dialog>
          </CardTitle>

          <CardDescription>
            {t("advancedConditions.description")}
          </CardDescription>
        </div>

        {/* Bouton pour ajouter une condition */}
        <Button
          type="button"
          variant="outline"
          onClick={addCondition}
          className="w-fit"
          disabled={conditions.length >= 5}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("advancedConditions.addCondition")}
        </Button>
      </div>
      <CardContent className="space-y-4">
        {/* Information sur les conditions multiples */}
        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
          <div className="text-sm text-blue-900 dark:text-blue-100">
            💡 {t("advancedConditions.multipleInfo")}
          </div>
        </div>

        {/* Opérateur logique entre conditions (si plusieurs) */}
        {conditions.length > 1 && (
          <div className="space-y-2 border-b pb-4">
            <Label>{t("advancedConditions.logicalOperator")}</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {t("advancedConditions.operatorBetweenConditions")}
            </div>
            {/* Afficher les opérateurs pour chaque paire de conditions */}
            <div className="flex flex-wrap gap-4 items-center">
              {conditions.slice(0, -1).map((_, index) => {
                const ops = (values.conditionsOperators || []) as string[];
                const current = ops[index] || "AND";

                return (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {t("advancedConditions.condition")} {index + 1}
                    </Badge>
                    <Select
                      value={current}
                      onValueChange={(value) => {
                        const next = [...ops];
                        while (next.length < conditions.length - 1)
                          next.push("AND");
                        next[index] = value;
                        formik.setFieldValue("conditionsOperators", next);
                      }}
                    >
                      <SelectTrigger className="w-fit min-w-[80px]">
                        <SelectValue>
                          {current === "AND" ? (
                            <Badge variant="secondary">
                              {t("advancedConditions.operators.and.label")}
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              {t("advancedConditions.operators.or.label")}
                            </Badge>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {t("advancedConditions.operators.and.label")}
                            </Badge>
                            <span>
                              {t(
                                "advancedConditions.operators.and.description",
                              )}
                            </span>
                          </div>
                        </SelectItem>
                        <SelectItem value="OR">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {t("advancedConditions.operators.or.label")}
                            </Badge>
                            <span>
                              {t("advancedConditions.operators.or.description")}
                            </span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Badge variant="outline" className="text-xs">
                      {t("advancedConditions.condition")} {index + 2}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Liste des conditions avec système de tabs si plusieurs */}
        {conditions.length === 1 ? (
          // Une seule condition : affichage direct sans tabs
          <ConditionItem
            formik={formik}
            conditionIndex={0}
            onDelete={() => deleteCondition(0)}
            canDelete={false}
          />
        ) : (
          // Plusieurs conditions : utiliser les tabs
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
              {conditions.map((condition, index) => (
                <div key={condition.id} className="flex items-center gap-1">
                  <TabsTrigger
                    value={`condition-${index}`}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-3 w-3" />
                    {t("advancedConditions.condition")} {index + 1}
                    {conditions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCondition(index);
                        }}
                        className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-2 w-2" />
                      </Button>
                    )}
                  </TabsTrigger>
                </div>
              ))}
            </TabsList>

            {conditions.map((_, index) => (
              <TabsContent key={index} value={`condition-${index}`}>
                <ConditionItem
                  formik={formik}
                  conditionIndex={index}
                  onDelete={() => deleteCondition(index)}
                  canDelete={conditions.length > 1}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Affichage des erreurs de validation */}
        {errors?.conditions && touched?.conditions && (
          <div className="text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {typeof errors.conditions === "string"
              ? errors.conditions
              : t("advancedConditions.validationError")}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
